import React, { useRef, useReducer, useEffect, useState } from 'react';
import Map from './components/map';
import Sidebar, { sidebarWidth } from './components/sidebar';
import useComponentSize from '@rehooks/component-size';
import { Global, css } from '@emotion/core';
import {
  reducer,
  initialState,
  setSubmissions,
  setFrogs,
  selectShowTable,
  setTab,
  resetToInitialState,
} from './modules/app-duck';
import { StateContext } from './hooks/use-global-state';
import { globalStyles, colors, smallMobileMQ } from './modules/theme';
import {
  fetchJson,
  fetchCsv,
  API_URL
} from './modules/api';
import { H1, P, Span, pixelToRem } from './components/ui';
import MapTable from './components/map-table';
import MapInfo from './components/map-info';
import { useIsSmallMobile } from './hooks/mobile';
import MobileFilters from './components/mobile-filters';
import RecordTab from './components/record-tabs';
import {
  faCaretDown,
  faCaretUp,
  faDownload,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const App = () => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const mainRef = useRef(null);
  const mainSize = useComponentSize(mainRef);
  const infoRef = useRef(null);
  const infoSize = useComponentSize(infoRef);
  const tab = state.tab;
  useEffect(() => {
    dispatch(resetToInitialState());
    if (tab === 'ALL') {
      fetchJson('captures.json.gz')
        .then(x => {
          dispatch(setSubmissions(x));
        })
        .then(d => {
          // just fetch this so that the cache is warm :)
          fetchCsv('FrogID3_final_dataset.csv');
        });
    } else {
      fetchCsv('FrogID3_final_dataset.csv').then(x => {
        dispatch(setSubmissions(x));
      });
    }
  }, [tab]);
  useEffect(() => {
    fetchJson('frogs.json.gz').then(x => {
      dispatch(setFrogs(x));
    });
    return;
  }, []);
  const showTable = selectShowTable(state);
  const isSmallMobile = useIsSmallMobile();

  return (
    <div
      css={{
        width: '100%',
        maxWidth: 1140,
      }}
      className={'smu-root'}
    >
      <StateContext.Provider value={{ state, dispatch }}>
        <div css={{ textAlign: 'center' }}>
          <H1
            css={{
              textAlign: 'center',
              marginBottom: 30,
              [smallMobileMQ]: {
                fontSize: pixelToRem(22),
                lineHeight: '26pt',
                marginBottom: 5,
              },
            }}
          >
            Explore FrogID records
          </H1>
          <P
            css={{
              fontWeight: 'normal',
              marginBottom: 20,
              fontSize: pixelToRem(16),
              lineHeight: '22pt',
            }}
          >
            Filter frog calls by council area and species
          </P>
          <RecordTab
            onChange={x => {
              dispatch(setTab(x));
            }}
            value={tab}
          />
          <div
            css={{ marginBottom: 36, color: '#8D8D8D', fontWeight: 'normal' }}
          >
            <Span
              css={{
                display: 'block',
              }}
            >
              {tab === 'ALL'
                ? 'Latest frog call submissions'
                : 'Verified FrogID data set'}{' '}
            </Span>
              <Span
                css={{
                  marginBottom: 46,
                  display: 'block',
                  maxWidth: 650,
                  lineHeight: '14pt',
                  margin: 'auto',
                  fontWeight: 'normal',
                }}
              >
                {tab === 'ALL'
                  ? 'Live data feed of Records exclude threatened frog species. Threatened species data becomes available in annual releases of FrogID Records.'
                  : 'Threatened species have buffered results. Please see data paper regarding buffered results, and contact the FrogID team to request unbuffered data for annually released Records.'}
              </Span>
          </div>
        </div>
        {isSmallMobile && <MobileFilters />}

        <div
          ref={mainRef}
          css={{
            display: 'flex',
            height: isSmallMobile ? 'auto' : '88vh',
            maxHeight: 880,
            width: '100%',
            position: 'relative',
          }}
        >
          <Global styles={css(globalStyles)} />
          {mainSize.width && (
            <div
              css={{
                width: isSmallMobile
                  ? mainSize.width
                  : mainSize.width - (sidebarWidth + 5),
              }}
            >
              <Map
                width={
                  isSmallMobile
                    ? mainSize.width
                    : mainSize.width - (sidebarWidth + 5)
                }
                height={
                  isSmallMobile
                    ? Math.min(mainSize.height - infoSize.height - 2, 400)
                    : mainSize.height - infoSize.height - 2
                }
              />
              <div
                ref={infoRef}
                css={{
                  minHeight: 139,
                  marginTop: 2,
                  background: colors.darkBlue,
                  borderRadius: '4px',
                }}
              >
                {showTable ? <MapTable /> : <MapInfo />}
              </div>
            </div>
          )}

          {!isSmallMobile && <Sidebar />}
        </div>
        <div css={{ marginTop: 60 }}>
          <a
            href={API_URL + '/FrogID3_final_dataset.csv'}
            download
            css={{
              background: colors.orange,
              display: 'flex',
              borderRadius: '4px',
              margin: '0 auto 20px auto',
              lineHeight: '26pt',
              width: 300,
              color: 'white',
              textDecoration: 'none',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FontAwesomeIcon
              css={{ fontSize: '12px', marginRight: 12 }}
              icon={faDownload}
            />
            <span
              css={{
                fontSize: pixelToRem(18),

                fontWeight: 'bold',
              }}
            >
              FrogID dataset 3.0*
            </span>
          </a>
          <p
            css={{
              textAlign: 'center',
              color: colors.gray,
              margin: '0 auto',
              fontSize: pixelToRem(14),
              lineHeight: '18px',
            }}
          >
            <em>*</em> 10 Nov 2017 to 9 Nov 2020
          </p>
        </div>
      </StateContext.Provider>
    </div>
  );
};

export default App;
